import { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ButtonOne from "./normal-button";
import typography from "../cssUtils/typography";
import { ColorsConstants, grayFontColor } from "../cssUtils/constants";

/**
 * FileDropzone — drag-and-drop area with a "Choose file" button.
 *
 * Props:
 *  - onFileSelected: (file: File) => void — called with the first file dropped or chosen
 *  - accept: string — passed to the file input, e.g. ".csv,.xlsx,.xls"
 *  - title / hint / buttonLabel: texts
 *  - error: string — red border and message under the hint
 *  - disabled: boolean — ignores drops and disables the button
 */
const FileDropzone = ({
  onFileSelected,
  accept,
  title = "Drag your file here",
  hint,
  buttonLabel = "Choose file",
  error = "",
  disabled = false,
  sx = {},
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = (files) => {
    const file = files && files[0];
    if (file && !disabled) onFileSelected(file);
  };

  return (
    <Box
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        pick(event.dataTransfer.files);
      }}
      sx={{
        border: `2px dashed ${error ? ColorsConstants.error : dragging ? ColorsConstants.secondaryBackgroundColor : ColorsConstants.inputFieldGrey}`,
        backgroundColor: dragging ? ColorsConstants.primaryBlue60 : "transparent",
        borderRadius: "10px",
        p: 4,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        ...sx,
      }}
    >
      <UploadFileIcon sx={{ color: ColorsConstants.primaryBlue90, fontSize: 40 }} />
      <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>{title}</Typography>
      {hint && <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>{hint}</Typography>}
      {error && (
        <Typography role="alert" sx={{ ...typography.bodySmall, color: ColorsConstants.error }}>
          {error}
        </Typography>
      )}
      <ButtonOne variant="outlined" disabled={disabled} onClick={() => inputRef.current?.click()} sx={{ mt: 1 }}>
        {buttonLabel}
      </ButtonOne>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        data-testid="file-dropzone-input"
        onChange={(event) => {
          pick(event.target.files);
          event.target.value = ""; // choosing the same file again must still fire
        }}
      />
    </Box>
  );
};

export default FileDropzone;
